import React, { useState } from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { reasonLabel } from '../../constants/shipments';
import { shipmentsApi } from '../../utils/shipmentsApi';
import { formatDateTime, relativeTime } from '../../utils/shipmentsFormat';
import { Banner } from './Ui';
import { useRemote } from './useRemote';

const OUTCOMES = { error: 'Chyba, zkusí se znovu', failed: 'Selhalo trvale' };

// Messages the worker could not process (GET /errors) with a "Zkusit znovu" button per message.
const ErrorsCard = ({ notify }) => {
  const errors = useRemote('errors', () => shipmentsApi.errors({ limit: 50 }));
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const items = errors.data?.items || [];

  const retry = async (it) => {
    const key = `${it.mailbox}:${it.messageId}`;
    setBusyKey(key);
    setError('');
    try {
      await shipmentsApi.retryError(it.mailbox, it.messageId);
      notify.success('Zpráva se zpracuje znovu při nejbližším běhu.');
      errors.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyKey('');
    }
  };

  return (
    <section className="shp-card" aria-labelledby="shp-errors-title">
      <h2 id="shp-errors-title" className="shp-h2"><AlertTriangle size={18} color="#E53935" aria-hidden="true" /> Log chyb zpracování{errors.data ? ` (${errors.data.total})` : ''}</h2>
      {errors.error && <Banner tone="bad" action={<button type="button" className="shp-textbtn" onClick={errors.reload}>Zkusit znovu</button>}>Log se nepodařilo načíst. {errors.error.message}</Banner>}
      {error && <Banner tone="bad">{error}</Banner>}
      {errors.loaded && !errors.error && items.length === 0 && <p className="shp-muted" style={{ margin: 0 }}>Žádné chyby. Všechny zprávy se zpracovaly.</p>}
      {items.length > 0 && (
        <div className="shp-tablewrap">
          <table className="shp-ctable shp-ctable--stack">
            <thead>
              <tr><th scope="col">Schránka</th><th scope="col">Stav</th><th scope="col">Pokusy</th><th scope="col">Další pokus</th><th scope="col">Chyba</th><th scope="col">Akce</th></tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const key = `${it.mailbox}:${it.messageId}`;
                return (
                  <tr key={key}>
                    <td data-label="Schránka"><span className="shp-wrap" style={{ display: 'grid', gap: 2 }}>{it.mailbox}{it.fromDomain && <span className="shp-small shp-muted">od {it.fromDomain}</span>}<span className="shp-small shp-muted" title={formatDateTime(it.updatedAt)}>{relativeTime(it.updatedAt)}</span></span></td>
                    <td data-label="Stav"><span className={`shp-tag ${it.outcome === 'failed' ? 'shp-tag--bad' : 'shp-tag--warn'}`}>{OUTCOMES[it.outcome] || it.outcome}</span></td>
                    <td data-label="Pokusy" className="shp-num">{it.attempts}</td>
                    <td data-label="Další pokus">{it.nextRetryAt ? <time dateTime={it.nextRetryAt}>{formatDateTime(it.nextRetryAt)}</time> : <span className="shp-muted">–</span>}</td>
                    <td data-label="Chyba" style={{ maxWidth: 360 }}>
                      <span className="shp-wrap" dir="auto">{it.lastError || '–'}</span>
                      {Array.isArray(it.reasons) && it.reasons.length > 0 && (
                        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>{it.reasons.map((r) => <span key={r} className="shp-tag">{reasonLabel(r)}</span>)}</span>
                      )}
                    </td>
                    <td data-label="Akce">
                      <button type="button" className="shp-textbtn" disabled={busyKey === key} onClick={() => retry(it)} aria-label={`Zkusit znovu zpracovat zprávu ze schránky ${it.mailbox}`}>
                        <RotateCw size={14} aria-hidden="true" /> Zkusit znovu
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default ErrorsCard;
